import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { sortDepots } from '@/features/events/depot-format';
import {
  DepotEvent,
  EventListParams,
  PaginatedResponse,
  CreateEventRequest,
  UpdateEventRequest,
  CreateEventSeriesRequest,
  CreateEventSeriesResult,
  RejectEventRequest,
  ReopenEventRequest,
  NotifyAdvertisingDepotsResult,
  EventType,
  PresetText,
  SalesChannel,
  DepotRef,
  EventStatus,
  Mailing,
  SaveMailingRequest,
  ImportJobStatusFile,
  EventText,
  SaveEventTextRequest,
  CancellationTerm,
  SaveCancellationTermRequest,
  PaymentNoteTemplate,
  SavePaymentNoteTemplateRequest,
  FinancialSupport,
  SaveFinancialSupportRequest,
  RegistrationNoteTemplate,
  SaveRegistrationNoteTemplateRequest,
  Partner,
  ParticipantsResponse,
  RefundView,
  RefundDraft,
  AddRefundParticipantInput,
} from '@/types';

const EVENTS_KEY = 'events';
const EVENT_TYPES_KEY = 'event-types';
const DEPOTS_KEY = 'depots';
const MAILINGS_KEY = 'mailings';
const EVENT_TEXTS_KEY = 'event-texts';
const CANCELLATION_TERMS_KEY = 'cancellation-terms';
const PAYMENT_NOTE_TEMPLATES_KEY = 'payment-note-templates';
const FINANCIAL_SUPPORT_KEY = 'financial-support';
const REGISTRATION_NOTE_TEMPLATES_KEY = 'registration-note-templates';
const PARTNERS_KEY = 'partners';

export function useEvents(params: EventListParams, options?: { enabled?: boolean }) {
  return useQuery<PaginatedResponse<DepotEvent>>({
    queryKey: [EVENTS_KEY, params],
    queryFn: () => apiClient.getEvents(params),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
    enabled: options?.enabled,
  });
}

export function useEvent(identifier: string | null) {
  return useQuery<DepotEvent>({
    queryKey: [EVENTS_KEY, identifier],
    queryFn: () => apiClient.getEvent(identifier!),
    enabled: !!identifier,
  });
}

export function useEventTypes() {
  return useQuery<EventType[]>({
    queryKey: [EVENT_TYPES_KEY],
    queryFn: () => apiClient.getEventTypes(),
    staleTime: 5 * 60_000,
  });
}

export function usePresetTexts() {
  return useQuery<PresetText[]>({
    queryKey: ['event-preset-texts'],
    queryFn: () => apiClient.getPresetTexts(),
    staleTime: 5 * 60_000,
  });
}

export function useSalesChannels() {
  return useQuery<SalesChannel[]>({
    queryKey: ['sales-channels'],
    queryFn: () => apiClient.getSalesChannels(),
    staleTime: 5 * 60_000,
  });
}

export function useMailings() {
  return useQuery<Mailing[]>({
    queryKey: [MAILINGS_KEY],
    queryFn: () => apiClient.getMailings(),
    staleTime: 5 * 60_000,
  });
}

/**
 * Uploads a new mailing spreadsheet (admin only).
 *
 * Invalidates the mailing list on success so every open event dialog picks up the
 * new schedule without a reload.
 *
 * Note: `useMailings` has a five-minute staleTime, so an event dialog open in
 * another tab keeps its copy until then. Pre-existing and accepted — lowering the
 * staleTime would re-fetch the schedule on every dialog open for no real gain.
 */
export function useImportMailings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => apiClient.importMailings(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MAILINGS_KEY] });
    },
  });
}

/** Legt ein Mailing an oder ändert es (Zentrale/Admin). */
export function useSaveMailing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: SaveMailingRequest) => apiClient.saveMailing(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MAILINGS_KEY] });
    },
  });
}

/**
 * Löscht ein oder mehrere Mailings (Zentrale/Admin).
 *
 * Liegt bewusst hier statt in einem eigenen Service: `MAILINGS_KEY` wohnt in dieser
 * Datei, und ein zweites Modul auf demselben Schlüssel ist der Anfang von
 * Cache-Fehlern.
 */
export function useDeleteMailings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (labels: string[]) => apiClient.deleteMailings(labels),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MAILINGS_KEY] });
    },
  });
}

// --- VA-Massenimport (05.08.2026) -----------------------------------------

const EVENT_IMPORT_JOBS_KEY = 'event-import-jobs';

/**
 * Job-Übersicht mit Polling, solange ein Job `running` ist.
 *
 * Erster Polling-Hook im Projekt: der Worker läuft in einer separaten Lambda,
 * es gibt keinen Push-Kanal (WebSocket/SSE) dafür — der Nutzer muss den
 * Fortschritt sonst per Neuladen abfragen, das Polling übernimmt das für ihn.
 * 3s statt der eher üblichen 1s, weil der Worker nach jeder ZEILE speichert
 * (nicht nach jedem Batch) — bei hunderten Zeilen wäre ein 1s-Takt viele
 * Anfragen für eine Fortschrittsanzeige, die niemand Sekunde für Sekunde
 * beobachtet.
 */
export function useEventImportJobs() {
  return useQuery<ImportJobStatusFile[]>({
    queryKey: [EVENT_IMPORT_JOBS_KEY],
    queryFn: () => apiClient.listEventImportJobs(),
    refetchInterval: (query) => {
      const jobs = query.state.data;
      const anyRunning = jobs?.some((job) => job.status === 'running');
      return anyRunning ? 3_000 : false;
    },
  });
}

export function useUploadEventImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      jobId,
      filename,
      vaType,
      targetStatus,
    }: {
      /** From a prior useUploadZip() call — the .zip is already in S3 under this jobId. */
      jobId: string;
      filename: string;
      vaType: string;
      targetStatus: 'initial' | 'approved';
    }) => apiClient.uploadEventImport(jobId, filename, vaType, targetStatus),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EVENT_IMPORT_JOBS_KEY] });
    },
  });
}

/**
 * Presigned S3-Upload für das kombinierte Import-ZIP (Excel-Vorlage +
 * Flyer-PDFs, 18.-20.09.2026) — zwei Schritte in einer Mutation: URL
 * anfordern, dann direkt zu S3 hochladen. Gibt die jobId zurück, die der
 * Aufrufer in useUploadEventImport() weiterreicht, damit beide Schritte zu
 * demselben Job gehören.
 */
export function useUploadZip() {
  return useMutation({
    mutationFn: async (zipFile: File): Promise<string> => {
      const { jobId, uploadUrl } = await apiClient.requestUploadUrl();
      await apiClient.uploadZip(uploadUrl, zipFile);
      return jobId;
    },
  });
}

export function useStartEventImportJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => apiClient.startEventImportJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EVENT_IMPORT_JOBS_KEY] });
    },
  });
}

export function useStopEventImportJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => apiClient.stopEventImportJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EVENT_IMPORT_JOBS_KEY] });
    },
  });
}

/**
 * Rückgängig — invalidiert zusätzlich die VA-Liste (EVENTS_KEY): ein
 * erfolgreicher Rollback löscht reale Veranstaltungen, die eine offene
 * Übersichtsseite sonst noch zeigen würde.
 */
export function useRollbackEventImportJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => apiClient.rollbackEventImportJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EVENT_IMPORT_JOBS_KEY] });
      queryClient.invalidateQueries({ queryKey: [EVENTS_KEY] });
    },
  });
}

export function useDeleteEventImportJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => apiClient.deleteEventImportJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EVENT_IMPORT_JOBS_KEY] });
    },
  });
}

/**
 * Textvorlagen für den VA-Beschreibungstext.
 *
 * Zwei Verbraucher: die Pflegeseite und der VA-Dialog (dort für die Auswahlfelder
 * Kategorie und Vorlage). Derselbe Schlüssel für beide, damit eine Änderung in der
 * Pflege im Dialog sichtbar wird.
 */
/**
 * Die sichtbaren Textvorlagen.
 *
 * Die Sichtbarkeitsregel aus US-204 wendet der SERVER an (`visibleTemplates` im
 * Listen-Endpunkt): eine Depot-Rolle erhält die Vorlagen ihres Partners plus die der
 * Zentrale. Hier ist deshalb absichtlich kein Filter — dieselbe Liste versorgt die
 * Pflegeseite und die Auswahl im VA-Formular (US-205), und ein zweiter Filter im Browser
 * würde nur eine der beiden Stellen treffen und die Regel auseinanderlaufen lassen.
 */
export function useEventTexts() {
  return useQuery<EventText[]>({
    queryKey: [EVENT_TEXTS_KEY],
    queryFn: () => apiClient.getEventTexts(),
    staleTime: 5 * 60_000,
  });
}

/** Legt eine Vorlage an oder ändert sie (Zentrale/Admin). */
export function useSaveEventText() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: SaveEventTextRequest) => apiClient.saveEventText(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EVENT_TEXTS_KEY] });
    },
  });
}

/** Löscht eine oder mehrere Vorlagen (Zentrale/Admin). */
export function useDeleteEventTexts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => apiClient.deleteEventTexts(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EVENT_TEXTS_KEY] });
    },
  });
}

/**
 * Die sichtbaren Stornobedingungen (PRD-020 Thema 3, US-306).
 *
 * Wortgleich zu useEventTexts: die Sichtbarkeitsregel wendet der SERVER an
 * (`visibleCancellationTerms`), kein Filter hier — dieselbe Liste versorgt die
 * Pflegeseite und die Auswahl im Zahlungsabschnitt des VA-Formulars (US-304).
 */
export function useCancellationTerms() {
  return useQuery<CancellationTerm[]>({
    queryKey: [CANCELLATION_TERMS_KEY],
    queryFn: () => apiClient.getCancellationTerms(),
    staleTime: 5 * 60_000,
  });
}

/** Legt eine Stornobedingung an oder ändert sie — wortgleich zu useSaveEventText. */
export function useSaveCancellationTerm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: SaveCancellationTermRequest) => apiClient.saveCancellationTerm(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CANCELLATION_TERMS_KEY] });
    },
  });
}

/** Löscht eine oder mehrere Stornobedingungen — wortgleich zu useDeleteEventTexts. */
export function useDeleteCancellationTerms() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => apiClient.deleteCancellationTerms(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CANCELLATION_TERMS_KEY] });
    },
  });
}

/**
 * Die sichtbaren Zahlungstext-Vorlagen (Rich-Text-Umstellung, Teil 3).
 *
 * Wortgleich zu useCancellationTerms: keine Sichtbarkeitsregel hier, dieselbe Liste
 * versorgt die Pflegeseite und die Auswahl im Zahlungsabschnitt des VA-Formulars.
 */
export function usePaymentNoteTemplates() {
  return useQuery<PaymentNoteTemplate[]>({
    queryKey: [PAYMENT_NOTE_TEMPLATES_KEY],
    queryFn: () => apiClient.getPaymentNoteTemplates(),
    staleTime: 5 * 60_000,
  });
}

/** Legt eine Zahlungstext-Vorlage an oder ändert sie — wortgleich zu useSaveCancellationTerm. */
export function useSavePaymentNoteTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: SavePaymentNoteTemplateRequest) => apiClient.savePaymentNoteTemplate(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PAYMENT_NOTE_TEMPLATES_KEY] });
    },
  });
}

/** Löscht eine oder mehrere Zahlungstext-Vorlagen — wortgleich zu useDeleteCancellationTerms. */
export function useDeletePaymentNoteTemplates() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => apiClient.deletePaymentNoteTemplates(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PAYMENT_NOTE_TEMPLATES_KEY] });
    },
  });
}

/** Die Pflege "Finanzielle Unterstützung" — ohne Rollenwache, die Abrechnung braucht sie für jede Rolle. */
export function useFinancialSupport() {
  return useQuery<FinancialSupport[]>({
    queryKey: [FINANCIAL_SUPPORT_KEY],
    queryFn: () => apiClient.getFinancialSupport(),
    staleTime: 5 * 60_000,
  });
}

/** Legt einen Eintrag an oder ändert ihn — wortgleich zu useSavePaymentNoteTemplate. */
export function useSaveFinancialSupport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: SaveFinancialSupportRequest) => apiClient.saveFinancialSupport(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FINANCIAL_SUPPORT_KEY] });
    },
  });
}

/** Löscht einen oder mehrere Einträge — wortgleich zu useDeletePaymentNoteTemplates. */
export function useDeleteFinancialSupport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => apiClient.deleteFinancialSupport(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FINANCIAL_SUPPORT_KEY] });
    },
  });
}

/** Die sichtbaren Anmeldetext-Vorlagen — wortgleich zu usePaymentNoteTemplates. */
export function useRegistrationNoteTemplates() {
  return useQuery<RegistrationNoteTemplate[]>({
    queryKey: [REGISTRATION_NOTE_TEMPLATES_KEY],
    queryFn: () => apiClient.getRegistrationNoteTemplates(),
    staleTime: 5 * 60_000,
  });
}

/** Legt eine Anmeldetext-Vorlage an oder ändert sie — wortgleich zu useSavePaymentNoteTemplate. */
export function useSaveRegistrationNoteTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: SaveRegistrationNoteTemplateRequest) =>
      apiClient.saveRegistrationNoteTemplate(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REGISTRATION_NOTE_TEMPLATES_KEY] });
    },
  });
}

/** Löscht eine oder mehrere Anmeldetext-Vorlagen — wortgleich zu useDeletePaymentNoteTemplates. */
export function useDeleteRegistrationNoteTemplates() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => apiClient.deleteRegistrationNoteTemplates(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REGISTRATION_NOTE_TEMPLATES_KEY] });
    },
  });
}

/**
 * Die aktiven Depot-Partner, für die Auswahl beim Umordnen einer Vorlage (US-207).
 *
 * `enabled` liegt beim Aufrufer: der Server weist die Anfrage für jede Rolle außer
 * Zentrale/Admin mit 403 ab, und ein Abruf, der ohnehin scheitert, soll gar nicht erst
 * losgehen — sonst zeigt der Dialog kurz einen Fehlerzustand, bevor er das Feld verbirgt.
 */
export function usePartners(enabled: boolean) {
  return useQuery<Partner[]>({
    queryKey: [PARTNERS_KEY],
    queryFn: () => apiClient.getPartners(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * Teilnehmer einer VA. Lädt erst, wenn eine Id gesetzt ist (Modal geöffnet).
 *
 * Kurze staleTime: wer die Liste zum Ausdrucken öffnet, will den Stand von jetzt, nicht
 * den von vor fünf Minuten.
 */
export function useParticipants(eventId: string | null) {
  return useQuery<ParticipantsResponse>({
    queryKey: ['participants', eventId],
    queryFn: () => apiClient.getParticipants(eventId!),
    enabled: !!eventId,
    staleTime: 10_000,
  });
}

/**
 * Storniert ein Ticket aus der Teilnehmerliste. Danach stimmen Liste, Kontingent-Zahlen der
 * Übersicht und ein eventuell offener Abrechnungsentwurf nicht mehr mit dem alten Stand
 * überein — deshalb werden alle drei neu geladen.
 */
export function useCancelTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { eventId: string; ticketId: string }) =>
      apiClient.cancelTicket(vars.eventId, vars.ticketId),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['participants', vars.eventId] });
      queryClient.invalidateQueries({ queryKey: ['refund', vars.eventId] });
      queryClient.invalidateQueries({ queryKey: [EVENTS_KEY] });
    },
  });
}

// --- Abrechnung (DCP-2228, 08.09.2026) ---------------------------------------

export function useRefund(eventId: string | null) {
  return useQuery<RefundView>({
    queryKey: ['refund', eventId],
    queryFn: () => apiClient.getRefund(eventId!),
    enabled: !!eventId,
  });
}

export function useStartRefund() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) => apiClient.startRefund(eventId),
    onSuccess: (data, eventId) => queryClient.setQueryData(['refund', eventId], data),
  });
}

export function useSaveRefundDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      eventId: string;
      draft: Pick<RefundDraft, 'participants' | 'wines'>;
    }) => apiClient.saveRefundDraft(vars.eventId, vars.draft),
    onSuccess: (draft, vars) => {
      queryClient.setQueryData<RefundView | undefined>(['refund', vars.eventId], (prev) =>
        prev ? { ...prev, draft } : prev
      );
    },
  });
}

export function useSubmitRefund() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) => apiClient.submitRefund(eventId),
    onSuccess: (data, eventId) => queryClient.setQueryData(['refund', eventId], data),
  });
}

/** "Abrechnung freigeben & übertragen" (DCP-2228 follow-up, 16.09.2026). */
export function useTransferRefund() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) => apiClient.transferRefund(eventId),
    onSuccess: (data, eventId) => queryClient.setQueryData(['refund', eventId], data),
  });
}

/** "Tickets nachpflegen" (Abrechnung only, DCP-2228 09.09.2026) — creates a real ticket. */
export function useAddRefundParticipant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { eventId: string; input: AddRefundParticipantInput }) =>
      apiClient.addRefundParticipant(vars.eventId, vars.input),
    onSuccess: (data, vars) => queryClient.setQueryData(['refund', vars.eventId], data),
  });
}

export function useCancelRefundTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { eventId: string; ticketId: string }) =>
      apiClient.cancelRefundTicket(vars.eventId, vars.ticketId),
    onSuccess: (data, vars) => queryClient.setQueryData(['refund', vars.eventId], data),
  });
}

/** Schritt 3.3's preview — manual trigger, not useQuery: the wizard calls this once on entering the step. */
export function usePreviewRefund() {
  return useMutation({
    mutationFn: (eventId: string) => apiClient.previewRefund(eventId),
  });
}

/** Nachweis-Upload (DCP-2228 follow-up, 15.09.2026) — Antwort ist die volle RefundView. */
export function useUploadRefundProof() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { eventId: string; file: File }) => apiClient.uploadRefundProof(vars.eventId, vars.file),
    onSuccess: (data, vars) => queryClient.setQueryData(['refund', vars.eventId], data),
  });
}

/** Nachweis entfernen, ohne einen neuen hochzuladen (21.09.2026-Follow-up). */
export function useDeleteRefundProof() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) => apiClient.deleteRefundProof(eventId),
    onSuccess: (data, eventId) => queryClient.setQueryData(['refund', eventId], data),
  });
}

/**
 * Freigabe durch Admin/Zentrale (21.09.2026-Follow-up) — anders als
 * useUploadRefundProof/useDeleteRefundProof liefert der Endpunkt nur den
 * TicketRefund selbst zurück, nicht die volle RefundView, daher Invalidieren
 * statt direktem setQueryData. Invalidiert zusätzlich EVENTS_KEY: die
 * "Abrechnung"-Spalte in der Übersicht muss den neuen Status sofort zeigen.
 */
export function useApproveRefundProof() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) => apiClient.approveRefundProof(eventId),
    onSettled: (_data, _error, eventId) => {
      queryClient.invalidateQueries({ queryKey: ['refund', eventId] });
      queryClient.invalidateQueries({ queryKey: [EVENTS_KEY] });
    },
  });
}

/** Manual trigger (not useQuery) — the wizard calls this per search input, not per render. */
export function useSearchWineProducts() {
  return useMutation({
    mutationFn: (query: string) => apiClient.searchWineProducts(query),
  });
}

/** Resolved lazily, only when a wine is actually added to the Abrechnung draft. */
export function useResolveWineTaxCategory() {
  return useMutation({
    mutationFn: (sku: string) => apiClient.resolveWineTaxCategory(sku),
  });
}

/** Customer picker for "Tickets nachpflegen" — same manual-trigger shape as the wine search. */
export function useSearchCustomers() {
  return useMutation({
    mutationFn: (query: string) => apiClient.searchCustomers(query),
  });
}

export function useDepots() {
  return useQuery<DepotRef[]>({
    queryKey: [DEPOTS_KEY],
    // Aufsteigend nach Depotnummer (D001…D999) — die commerce-api liefert keine
    // erkennbare Ordnung, das war der eigentliche Grund für die zufällig wirkende
    // Reihenfolge in den Pickern.
    queryFn: async () => sortDepots(await apiClient.getDepots()),
    staleTime: 5 * 60_000,
  });
}

// ---- Mutations ---------------------------------------------------------------

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation<DepotEvent, Error, CreateEventRequest>({
    mutationFn: (req) => apiClient.createEvent(req),
    onSettled: () => qc.invalidateQueries({ queryKey: [EVENTS_KEY] }),
  });
}

/** POST /api/events/:id/series (24.09.2026) — see EventSeriesDialog.tsx. */
export function useCreateEventSeries() {
  const qc = useQueryClient();
  return useMutation<
    CreateEventSeriesResult,
    Error,
    { identifier: string; request: CreateEventSeriesRequest }
  >({
    mutationFn: ({ identifier, request }) => apiClient.createEventSeries(identifier, request),
    onSettled: () => qc.invalidateQueries({ queryKey: [EVENTS_KEY] }),
  });
}

export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation<DepotEvent, Error, UpdateEventRequest>({
    mutationFn: (req) => apiClient.updateEvent(req),
    onSettled: () => qc.invalidateQueries({ queryKey: [EVENTS_KEY] }),
  });
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (identifier) => apiClient.deleteEvent(identifier),
    onSettled: () => qc.invalidateQueries({ queryKey: [EVENTS_KEY] }),
  });
}

/**
 * Shared optimistic status-transition helper — mirrors useModerateReview:
 * cancelQueries → snapshot via getQueriesData → optimistic setQueriesData →
 * onError restore → onSettled invalidate.
 */
function useStatusTransition<TVars extends { identifier: string }>(
  mutationFn: (vars: TVars) => Promise<DepotEvent>,
  nextStatus: (vars: TVars) => EventStatus
) {
  const qc = useQueryClient();
  return useMutation<
    DepotEvent,
    Error,
    TVars,
    { previousQueries: [readonly unknown[], PaginatedResponse<DepotEvent> | undefined][] }
  >({
    mutationFn,
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: [EVENTS_KEY] });
      const previousQueries = qc.getQueriesData<PaginatedResponse<DepotEvent>>({
        queryKey: [EVENTS_KEY],
      });
      qc.setQueriesData<PaginatedResponse<DepotEvent>>(
        { queryKey: [EVENTS_KEY] },
        (old) => {
          if (!old || !old.items) return old;
          return {
            ...old,
            items: old.items.map((e) =>
              e.identifier === vars.identifier
                ? { ...e, status: nextStatus(vars) }
                : e
            ),
          };
        }
      );
      return { previousQueries };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousQueries) {
        for (const [key, data] of context.previousQueries) {
          qc.setQueryData(key, data);
        }
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: [EVENTS_KEY] }),
  });
}

export function useSubmitEvent() {
  return useStatusTransition<{ identifier: string }>(
    (vars) => apiClient.submitEvent(vars.identifier),
    () => EventStatus.InReview
  );
}

export function useApproveEvent() {
  return useStatusTransition<{ identifier: string }>(
    (vars) => apiClient.approveEvent(vars.identifier),
    () => EventStatus.Approved
  );
}

export function useRejectEvent() {
  return useStatusTransition<RejectEventRequest>(
    (vars) => apiClient.rejectEvent(vars),
    () => EventStatus.Initial
  );
}

export function useReopenEvent() {
  return useStatusTransition<ReopenEventRequest>(
    (vars) => apiClient.reopenEvent(vars),
    (vars) => vars.toStatus ?? EventStatus.InReview
  );
}

export function useCancelEvent() {
  return useStatusTransition<{ identifier: string }>(
    (vars) => apiClient.cancelEvent(vars.identifier),
    () => EventStatus.Cancelled
  );
}

export function useCompleteEvent() {
  return useStatusTransition<{ identifier: string }>(
    (vars) => apiClient.completeEvent(vars.identifier),
    () => EventStatus.Completed
  );
}

/**
 * Notifies advertising depots after approval — a separate confirmation step, not
 * a status transition, so it does not go through useStatusTransition: the
 * event's status is untouched either way.
 */
export function useNotifyAdvertisingDepots() {
  return useMutation<NotifyAdvertisingDepotsResult, Error, string>({
    mutationFn: (identifier) => apiClient.notifyAdvertisingDepots(identifier),
  });
}
